<template>
  <v-container
    fluid
    class="ac2-page">
    <v-row>
      <v-col
        cols="12"
        :md="selectItem && showDetail ? '8' : '12'">
        <v-card
          class="rounded-lg table-card">
          <v-data-table
            :headers="header"
            :items="items"
            :options.sync="options"
            :items-per-page="options.itemsPerPage"
            :server-items-length="total"
            :loading="loading"
            hide-default-footer
            class="w-full"
            @update:options="onOptionsUpdate"
          >
            <template v-slot:top>
              <div class="d-flex align-center flex-wrap px-4 pt-4 pb-2">
                <h6 class="text-h6 mb-0 mr-4">
                  AC2 Webhook Logs
                </h6>
                <v-spacer />
                <v-select
                  v-model="filterEvent"
                  :items="events"
                  item-text="text"
                  item-value="value"
                  label="Event"
                  color="secondary"
                  class="flex-grow-0 mr-3"
                  style="max-width: 180px;"
                  outlined
                  dense
                  hide-details>
                </v-select>
                <v-select
                  v-model="filterStatus"
                  :items="status"
                  item-text="text"
                  item-value="value"
                  label="สถานะ"
                  color="secondary"
                  class="flex-grow-0 mr-3"
                  style="max-width: 180px;"
                  outlined
                  dense
                  hide-details>
                </v-select>
                <search-box
                  v-model="inputSearch"
                  class="flex-grow-0"
                  style="max-width: 260px;"
                  @on-search="getData(true)" />
              </div>
              <v-divider />
            </template>

            <template #[`item.updatedAt`]="{ item }">
              <span style="white-space: nowrap;">
                {{ item.updatedAt | dateTimeUserFormat() }}
              </span>
            </template>
            <template #[`item.createdAt`]="{ item }">
              <span style="white-space: nowrap;">
                {{ item.createdAt | dateTimeUserFormat() }}
              </span>
            </template>
            <template #[`item.extNo`]="{ item }">
              <span style="white-space: nowrap;">
                {{ getExtNo(item) || '-' }}
              </span>
            </template>
            <template #[`item.status`]="{ item }">
              <div style="display: flex; flex-direction: row; align-items: center; gap: 0.2rem;">
                <v-icon :color="getStatusStyle(item.status).color">
                  {{ getStatusStyle(item.status).icon }}
                </v-icon>
                <span class="limit-text">
                  {{ item.status.toUpperCase() }}
                </span>
              </div>
            </template>
            <template #[`item.eventType`]="{ item }">
              <div style="display: flex; flex-direction: row; align-items: center; gap: 0.2rem;">
                <span class="limit-text">
                  {{ item.eventType }}
                </span>
              </div>
            </template>
            <template #[`item.errorCount`]="{ item }">
              <span
                :class="item.errorCount ? 'error--text' : 'grey--text'">
                {{ item.errorCount || 0 }}
              </span>
            </template>
            <template #[`item.option`]="{ item }">
              <v-btn
                link
                icon
                x-small
                :disabled="item.status === 'running' || item.status === 'forcing'"
                @click="onConfirmReRun(item)">
                <v-icon>
                  mdi-play-outline
                </v-icon>
              </v-btn>
              <v-btn
                link
                icon
                x-small
                :disabled="showDetail && selectItem && selectItem.id === item.id"
                @click="showItemDetail(item)">
                <v-icon>
                  mdi-magnify
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <v-divider />
          <div class="d-flex align-center flex-wrap px-4 py-3">
            <span class="text-caption grey--text">
              Total {{ total }} items
            </span>
            <v-spacer />
            <v-pagination
              v-model="options.page"
              :length="pageCount"
              :total-visible="5"
              color="secondary"
              @input="getData()" />
          </div>
        </v-card>
      </v-col>
      <v-col
        v-if="selectItem && showDetail"
        cols="12"
        md="4">
        <v-card class="rounded-lg detail-card pa-4">
          <div style="display: flex; flex-direction: row; align-items: center; gap: 0.2rem;">
            <v-icon :color="getStatusStyle(selectItem.status).color">
              {{ getStatusStyle(selectItem.status).icon }}
            </v-icon>
            <h3 class="text-subtitle-1 font-weight-bold mb-0">
              {{ selectItem.eventType }}
            </h3>
          </div>
          <p class="mb-0 mt-2">
            <b>Event </b> {{ selectItem.event }}
          </p>
          <p class="mb-0">
            <b>Date </b>{{ selectItem.createdAt | dateTimeUserFormat() }}
          </p>
          <p class="mb-0">
            <b>Last update </b>{{ selectItem.updatedAt | dateTimeUserFormat() }}
          </p>
          <p class="mb-0">
            <b>Request ID </b> {{ selectItem.requestId }}
          </p>
          <p
            v-if="getExtNo(selectItem)"
            class="mb-0">
            <b>Document No. </b> {{ getExtNo(selectItem) }}
          </p>
          <p
            v-if="selectItem.note"
            class="mb-0 error--text">
            <b>Note </b> {{ selectItem.note }}
          </p>
          <div class="mt-4">
            <a @click="scrollTo('Info')">#Info</a>
            <a
              class="ml-4"
              @click="scrollTo('RawData')">#Raw Data</a>
            <a
              class="ml-4"
              @click="scrollTo('Result')">#Result</a>
          </div>
          <p
            ref="Info"
            class="mt-4 mb-1 font-weight-medium">
            Info
          </p>
          <div class="json-box">
            <json-viewer
              :value="{
                event: selectItem.event,
                eventType: selectItem.eventType,
                requestId: selectItem.requestId,
                webhookId: selectItem.webhookId,
                owner: selectItem.owner,
                shopId: selectItem.shopId,
                signatureValid: selectItem.signatureValid,
                errorCount: selectItem.errorCount,
                processedAt: selectItem.processedAt,
                status: selectItem.status,
                createdBy: selectItem.createdBy,
                updatedBy: selectItem.updatedBy
              }"
              :expand-depth="1"
              copyable />
          </div>
          <p
            ref="RawData"
            class="mt-4 mb-1 font-weight-medium">
            Raw Data
          </p>
          <div class="json-box">
            <json-viewer
              :value="selectItem.rawData || {}"
              :expand-depth="5"
              copyable
              sort />
          </div>
          <p
            ref="Result"
            class="mt-4 mb-1 font-weight-medium">
            Result
          </p>
          <div class="json-box">
            <json-viewer
              :value="selectItem.result || {}"
              :expand-depth="5"
              copyable
              sort />
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import SearchBox from '@/components/SearchBox.vue'
import Ac2WebhookLogsProvider from '@/resources/Ac2WebhookLogsProvider'

const Ac2WebhookLogsService = new Ac2WebhookLogsProvider()

export default {
  components: {
    SearchBox
  },
  data () {
    return {
      inputSearch: '',
      filterStatus: 'failed',
      filterEvent: null,
      events: [
        {
          text: 'All',
          value: null
        },
        {
          text: 'Receipt Closed',
          value: 'receipt-closed'
        },
        {
          text: 'Order Status',
          value: 'order_status'
        },
        {
          text: 'Inventory Adjustment',
          value: 'inventory-adjustment'
        }
      ],
      status: [
        {
          text: 'All',
          value: null,
          color: 'warning',
          icon: 'mdi-help-circle-outline'
        },
        {
          text: 'Success',
          value: 'success',
          color: 'success',
          icon: 'mdi-check'
        },
        {
          text: 'Failed',
          value: 'failed',
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        },
        {
          text: 'Deleted',
          value: 'deleted',
          color: 'error',
          icon: 'mdi-delete-outline'
        }
      ],
      loading: false,
      selectItem: null,
      showDetail: false,
      total: 0,
      totalPages: 0,
      sortSnapshot: null,
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['createdAt'],
        sortDesc: [false]
      },
      header: [
        {
          text: 'Last update',
          value: 'updatedAt'
        },
        {
          text: 'Date',
          value: 'createdAt'
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false
        },
        {
          text: 'Event Type',
          value: 'eventType',
          sortable: false
        },
        {
          text: 'Event',
          value: 'event',
          sortable: false
        },
        {
          text: 'Document No.',
          value: 'extNo',
          sortable: false
        },
        {
          text: 'Error',
          value: 'errorCount',
          align: 'center',
          sortable: false,
          width: '70px'
        },
        {
          text: 'Options',
          value: 'option',
          align: 'center',
          sortable: false,
          width: '90px'
        }
      ],
      items: []
    }
  },
  computed: {
    pageCount () {
      return this.totalPages || 1
    }
  },
  watch: {
    filterStatus () {
      this.getData(true)
    },
    filterEvent () {
      this.getData(true)
    }
  },
  created () {
    this.getData(true)
  },
  methods: {
    ...mapActions({
      setModal: 'Components/setModal'
    }),
    onOptionsUpdate () {
      const sortKey = `${this.options.sortBy.join(',')}|${this.options.sortDesc.join(',')}`
      if (this.sortSnapshot === null) {
        this.sortSnapshot = sortKey
        return
      }
      if (sortKey !== this.sortSnapshot) {
        this.sortSnapshot = sortKey
        this.getData(true)
      }
    },
    showItemDetail (item) {
      this.selectItem = item
      this.showDetail = true
    },
    onConfirmReRun (item) {
      this.setModal({
        value: true,
        title: 'Re-run Webhook',
        message: 'Do you want to re-run this webhook',
        confirmText: 'Sure',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.reRun(item)
      })
    },
    async reRun (item) {
      try {
        this.loading = true

        await Ac2WebhookLogsService.manualWebhookLogById(item.id)

        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Re-run webhook success.',
          type: 'success'
        })

        this.getData(true)
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    scrollTo (tag) {
      const el = this.$refs[tag]
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    },
    getExtNo (item) {
      const raw = item?.rawData || {}
      const data = raw.data || {}
      return raw.extOrderNo1
        || raw.extRecptNo1
        || data.extOrderNo1
        || data.extRecptNo1
        || ''
    },
    getStatusStyle (status) {
      const found = this.status.find((s) => s.value === status)

      return {
        color: found?.color || 'warning',
        icon: found?.icon || 'mdi-help-circle-outline'
      }
    },
    async getData (clear = false) {
      this.showDetail = false

      if (clear) {
        this.options.page = 1
      }

      try {
        this.loading = true
        const { data: { results, totalDocs, totalPages } } = await Ac2WebhookLogsService.getWebhookLogs(
          {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            sortBy: this.options.sortBy[0],
            sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc',
            status: this.filterStatus,
            event: this.filterEvent,
            search: this.inputSearch
          }
        )
        this.items = results
        this.total = totalDocs
        this.totalPages = totalPages
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
<style scoped>
.table-card,
.detail-card {
  height: calc(100vh - 96px);
  overflow-y: auto;
}
.json-box {
  border: thin solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}
.theme--dark .json-box {
  border-color: rgba(255, 255, 255, 0.12);
}
.limit-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
